import {
  ChessJsRulesPort,
  type CanonicalResult,
  type PromotionPiece,
  type RulesColor,
  type RulesPort,
  type RulesSnapshot,
  type UciMove,
} from '../rules';

export const GAME_SESSION_RECORD_VERSION = 1;

export type SessionMoveActor = 'hot-seat' | 'human' | 'computer';
export type HumanColorPreference = RulesColor | 'random';

export interface ComputerAiProfile {
  readonly id: string;
  readonly tierVersion: string;
  readonly engineBuild: string;
  readonly gameSeed: string;
}

/** Who controls each side in the current game. */
export type PlayerMode =
  | { readonly kind: 'hot-seat' }
  | {
      readonly kind: 'computer';
      readonly humanColor: RulesColor;
      /** Optional to keep records written before colour-choice persistence valid. */
      readonly humanColorPreference?: HumanColorPreference;
      readonly learnMode: boolean;
      readonly ai: ComputerAiProfile;
    };

export interface ClockSettings {
  readonly initialMs: number;
  readonly incrementMs: number;
}

/**
 * A deadline is held only for the side whose turn it is. The other remaining
 * value is a settled value from that side's last completed turn.
 */
export interface GameClockState extends ClockSettings {
  readonly whiteRemainingMs: number;
  readonly blackRemainingMs: number;
  readonly activeColor: RulesColor;
  readonly activeDeadlineMs: number;
}

export interface ClockProjection {
  readonly whiteRemainingMs: number;
  readonly blackRemainingMs: number;
  readonly activeColor: RulesColor;
  readonly activeDeadlineMs: number;
  readonly flagged: boolean;
}

export interface TimeoutResult {
  readonly terminal: true;
  readonly token: '1-0' | '0-1';
  readonly reason: 'timeout';
  readonly winner: RulesColor;
}

export interface ResignationResult {
  readonly terminal: true;
  readonly token: '1-0' | '0-1';
  readonly reason: 'resignation';
  readonly winner: RulesColor;
}

export interface AgreementDrawResult {
  readonly terminal: true;
  readonly token: '1/2-1/2';
  readonly reason: 'draw-agreement';
}

export type SessionResult = CanonicalResult | TimeoutResult | ResignationResult | AgreementDrawResult;

/** Metadata that binds a worker result to one exact position and game branch. */
export interface AiRequestMetadata {
  readonly requestId: number;
  readonly gameEpoch: number;
  readonly positionRevision: number;
  readonly positionFen: string;
  readonly ply: number;
  readonly profileId: string;
  readonly tierVersion: string;
  readonly engineBuild: string;
  readonly gameSeed: string;
}

export interface AiFailure {
  readonly code: 'empty-result' | 'invalid-uci' | 'illegal-move';
  readonly requestId: number;
}

export interface GameSessionAiState {
  readonly nextRequestId: number;
  readonly pending?: AiRequestMetadata;
  readonly lastFailure?: AiFailure;
}

export interface PromotionPending {
  readonly gameEpoch: number;
  readonly positionRevision: number;
  readonly color: RulesColor;
  readonly from: string;
  readonly to: string;
}

/**
 * Accepted history is intentionally value-only. `clockBefore` is retained so
 * learn-mode undo can restore a coherent deadline without asking the UI for
 * an elapsed interval.
 */
export interface AcceptedSessionMove {
  readonly uci: UciMove;
  readonly san: string;
  readonly actor: SessionMoveActor;
  readonly committedAtMs: number;
  readonly clockBefore?: GameClockState;
}

/** A branch-rewind clock baseline, proven at one retained history ply. */
export interface ClockTimelineAnchor {
  readonly ply: number;
  readonly atMs: number;
  readonly clock: GameClockState;
}

export interface ReviewState {
  readonly ply: number;
  readonly position: RulesSnapshot;
}

export interface GameSessionState {
  readonly gameId: string;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
  /** Changes whenever a timeline is replaced or rewound. */
  readonly gameEpoch: number;
  /** The sole live authoritative RulesPort projection. */
  readonly position: RulesSnapshot;
  readonly acceptedMoves: readonly AcceptedSessionMove[];
  readonly mode: PlayerMode;
  readonly result: SessionResult;
  readonly clock?: GameClockState;
  readonly clockTimelineAnchors: readonly ClockTimelineAnchor[];
  readonly promotionPending?: PromotionPending;
  readonly ai: GameSessionAiState;
  readonly review?: ReviewState;
}

export interface GameSessionSetup {
  readonly gameId?: string;
  readonly initialFen?: string;
  readonly mode: PlayerMode;
  readonly clock?: ClockSettings;
  /** Use an injected timestamp to keep the domain deterministic and testable. */
  readonly startedAtMs?: number;
}

/** Serializable persistence data. Current FEN is an index only, never replay input. */
export interface GameSessionRecord {
  readonly schemaVersion: typeof GAME_SESSION_RECORD_VERSION;
  readonly gameId: string;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
  readonly gameEpoch: number;
  readonly initialFen: string;
  readonly acceptedUciMoves: readonly UciMove[];
  readonly acceptedMoves: readonly AcceptedSessionMove[];
  readonly derivedFen: string;
  readonly result: SessionResult;
  readonly mode: PlayerMode;
  readonly clock?: GameClockState;
  /** Optional so valid records written before clock-chain validation remain readable. */
  readonly clockTimelineAnchors?: readonly ClockTimelineAnchor[];
  readonly ai: GameSessionAiState;
}

export type GameSessionEffect =
  | { readonly type: 'persist'; readonly record: GameSessionRecord }
  | { readonly type: 'request-ai'; readonly request: AiRequestMetadata }
  | { readonly type: 'cancel-ai'; readonly request: AiRequestMetadata };

export interface GameSessionTransition {
  readonly state: GameSessionState;
  readonly effects: readonly GameSessionEffect[];
}

export type GameSessionCommand =
  | {
      readonly type: 'submit-move';
      readonly gameEpoch: number;
      readonly positionRevision: number;
      readonly from: string;
      readonly to: string;
      readonly promotion?: PromotionPiece;
      readonly atMs?: number;
    }
  | {
      readonly type: 'choose-promotion';
      readonly gameEpoch: number;
      readonly positionRevision: number;
      readonly promotion: PromotionPiece;
      readonly atMs?: number;
    }
  | {
      readonly type: 'resolve-ai';
      readonly requestId: number;
      readonly gameEpoch: number;
      readonly positionRevision: number;
      readonly positionFen: string;
      readonly uci: string | null;
      readonly atMs?: number;
    }
  | { readonly type: 'undo-learn'; readonly gameEpoch: number; readonly atMs?: number }
  /** Ends the current live game for the player-controlled side. */
  | { readonly type: 'resign'; readonly gameEpoch: number; readonly atMs?: number }
  /** Records an accepted draw offer in a live local hot-seat game. */
  | { readonly type: 'agree-draw'; readonly gameEpoch: number; readonly atMs?: number }
  /** A stale or duplicated reset must never replace a newer live game. */
  | {
      readonly type: 'new-game';
      readonly expectedGameEpoch: number;
      readonly setup: GameSessionSetup;
      readonly atMs?: number;
    }
  /** A delayed IndexedDB read must be bound to the game it was requested from. */
  | {
      readonly type: 'restore-record';
      readonly expectedGameEpoch: number;
      readonly record: unknown;
      readonly atMs?: number;
    }
  /** Lets an explicit-promotion dialog close without committing an accidental move. */
  | {
      readonly type: 'cancel-promotion';
      readonly gameEpoch: number;
      readonly positionRevision: number;
    }
  | { readonly type: 'select-review-ply'; readonly gameEpoch: number; readonly ply: number }
  | { readonly type: 'exit-review'; readonly gameEpoch: number; readonly atMs?: number }
  /** Reconciles one observed deadline; repaint intervals should use selectClockProjection instead. */
  | { readonly type: 'reconcile-clock'; readonly atMs: number };

export type RulesPortFactory = () => RulesPort;

export interface GameSessionDependencies {
  readonly rulesFactory?: RulesPortFactory;
}

const UCI_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

/**
 * Owns the mutable RulesPort while exposing immutable, serializable state and
 * effects. It is deliberately React-free: an adapter dispatches commands,
 * runs effects, and renders selectors from the returned state.
 */
export class GameSession {
  private readonly rulesFactory: RulesPortFactory;
  private rules: RulesPort;
  private current: GameSessionState;
  private initialEffects: readonly GameSessionEffect[];

  public constructor(setup: GameSessionSetup, dependencies: GameSessionDependencies = {}) {
    this.rulesFactory = dependencies.rulesFactory ?? (() => new ChessJsRulesPort());
    this.rules = this.rulesFactory();

    if (!isSetup(setup)) {
      throw new Error('GameSession requires a valid player mode and optional clock settings.');
    }

    const startedAtMs = toTimestamp(setup.startedAtMs, 0);
    const reset = this.rules.reset(setup.initialFen);
    if (!reset.accepted) {
      throw new Error(`GameSession could not start: ${reset.reason}`);
    }

    const state = createState({
      gameId: setup.gameId ?? 'game-0',
      createdAtMs: startedAtMs,
      updatedAtMs: startedAtMs,
      gameEpoch: 0,
      position: reset.snapshot,
      mode: setup.mode,
      clock: setup.clock,
      nextRequestId: 1,
    });
    const requested = this.requestAiIfNeeded(state);
    this.current = requested.state;
    this.initialEffects = Object.freeze([
      persistEffect(this.current),
      ...requested.effects,
    ]);
  }

  /** The current state is a plain immutable-value projection. */
  public snapshot(): GameSessionState {
    return this.current;
  }

  /** Initial persistence and a possible opening AI request; returned once. */
  public takeInitialEffects(): readonly GameSessionEffect[] {
    const effects = this.initialEffects;
    this.initialEffects = Object.freeze([]);
    return effects;
  }

  public toRecord(): GameSessionRecord {
    return toGameSessionRecord(this.current);
  }

  public dispatch(command: GameSessionCommand): GameSessionTransition {
    const transition = this.reduce(command);
    this.current = transition.state;
    return transition;
  }

  private reduce(command: GameSessionCommand): GameSessionTransition {
    switch (command.type) {
      case 'submit-move':
        return this.submitMove(command);
      case 'choose-promotion':
        return this.choosePromotion(command);
      case 'resolve-ai':
        return this.resolveAi(command);
      case 'undo-learn':
        return this.undoLearn(command);
      case 'resign':
        return this.resign(command);
      case 'agree-draw':
        return this.agreeDraw(command);
      case 'new-game':
        return this.newGame(command);
      case 'restore-record':
        return this.restoreRecord(command);
      case 'cancel-promotion':
        return this.cancelPromotion(command);
      case 'select-review-ply':
        return this.selectReviewPly(command);
      case 'exit-review':
        return this.exitReview(command);
      case 'reconcile-clock':
        return this.reconcileClock(command.atMs);
    }
  }

  private submitMove(command: Extract<GameSessionCommand, { type: 'submit-move' }>): GameSessionTransition {
    if (!isLiveAt(this.current, command.gameEpoch, command.positionRevision)) {
      return unchanged(this.current);
    }

    const clockTransition = this.flagIfExpired(toTimestamp(command.atMs, this.current.updatedAtMs));
    if (clockTransition.state !== this.current) {
      return clockTransition;
    }

    if (this.current.promotionPending || !isHumanControlledTurn(this.current)) {
      return unchanged(this.current);
    }

    const before = this.current;
    const attempt = this.rules.submit({
      expectedRevision: before.position.revision,
      from: command.from,
      to: command.to,
      promotion: command.promotion,
    });

    if (!attempt.accepted) {
      if (attempt.reason !== 'promotion-required') {
        return unchanged(before);
      }

      const pending: PromotionPending = Object.freeze({
        gameEpoch: before.gameEpoch,
        positionRevision: before.position.revision,
        color: before.position.turn,
        from: command.from,
        to: command.to,
      });
      return changed(Object.freeze({ ...before, promotionPending: pending }));
    }

    return this.commitAcceptedMove(
      before,
      attempt.snapshot,
      attempt.value.uci,
      attempt.value.san,
      actorForTurn(before),
      toTimestamp(command.atMs, before.updatedAtMs),
    );
  }

  private choosePromotion(
    command: Extract<GameSessionCommand, { type: 'choose-promotion' }>,
  ): GameSessionTransition {
    const pending = this.current.promotionPending;
    if (
      pending === undefined ||
      !isLiveAt(this.current, command.gameEpoch, command.positionRevision) ||
      pending.gameEpoch !== command.gameEpoch ||
      pending.positionRevision !== command.positionRevision
    ) {
      return unchanged(this.current);
    }

    const clockTransition = this.flagIfExpired(toTimestamp(command.atMs, this.current.updatedAtMs));
    if (clockTransition.state !== this.current) {
      return clockTransition;
    }

    const before = this.current;
    const attempt = this.rules.submit({
      expectedRevision: before.position.revision,
      from: pending.from,
      to: pending.to,
      promotion: command.promotion,
    });
    if (!attempt.accepted) {
      return unchanged(before);
    }

    return this.commitAcceptedMove(
      before,
      attempt.snapshot,
      attempt.value.uci,
      attempt.value.san,
      actorForTurn(before),
      toTimestamp(command.atMs, before.updatedAtMs),
    );
  }

  private resolveAi(command: Extract<GameSessionCommand, { type: 'resolve-ai' }>): GameSessionTransition {
    const pending = this.current.ai.pending;
    if (pending === undefined || !matchesAiRequest(pending, command)) {
      return unchanged(this.current);
    }

    const clockTransition = this.flagIfExpired(toTimestamp(command.atMs, this.current.updatedAtMs));
    if (clockTransition.state !== this.current) {
      return clockTransition;
    }

    if (command.uci === null) {
      return this.aiFailed('empty-result', pending.requestId);
    }
    if (!isUci(command.uci)) {
      return this.aiFailed('invalid-uci', pending.requestId);
    }

    const before = this.current;
    if (!isComputerControlledTurn(before)) {
      return unchanged(before);
    }

    const attempt = this.rules.submit({
      expectedRevision: before.position.revision,
      from: command.uci.slice(0, 2),
      to: command.uci.slice(2, 4),
      ...(command.uci.length === 5
        ? { promotion: command.uci[4] as PromotionPiece }
        : {}),
    });
    if (!attempt.accepted) {
      return this.aiFailed('illegal-move', pending.requestId);
    }

    return this.commitAcceptedMove(
      before,
      attempt.snapshot,
      attempt.value.uci,
      attempt.value.san,
      'computer',
      toTimestamp(command.atMs, before.updatedAtMs),
    );
  }

  private undoLearn(command: Extract<GameSessionCommand, { type: 'undo-learn' }>): GameSessionTransition {
    if (
      command.gameEpoch !== this.current.gameEpoch ||
      this.current.review !== undefined ||
      this.current.result.terminal ||
      this.current.mode.kind !== 'computer' ||
      !this.current.mode.learnMode
    ) {
      return unchanged(this.current);
    }

    const atMs = toTimestamp(command.atMs, this.current.updatedAtMs);
    const clockTransition = this.flagIfExpired(atMs);
    if (clockTransition.state !== this.current) {
      return clockTransition;
    }

    const count = learnUndoCount(this.current);
    if (count === 0) {
      return unchanged(this.current);
    }

    const before = this.current;
    const earliestUndone = before.acceptedMoves[before.acceptedMoves.length - count];
    let snapshot = before.position;
    for (let index = 0; index < count; index += 1) {
      const undo = this.rules.undo(snapshot.revision);
      if (!undo.accepted) {
        return unchanged(before);
      }
      snapshot = undo.snapshot;
    }

    const history = Object.freeze(before.acceptedMoves.slice(0, -count));
    const restoredClock = earliestUndone?.clockBefore
      ? rebaseClock(earliestUndone.clockBefore, earliestUndone.committedAtMs, atMs)
      : before.clock;
    const clockTimelineAnchors = restoredClock && earliestUndone?.clockBefore
      ? Object.freeze([
          ...before.clockTimelineAnchors.filter((anchor) => anchor.ply < history.length),
          Object.freeze({ ply: history.length, atMs, clock: copyClock(restoredClock) }),
        ])
      : before.clockTimelineAnchors;
    const state: GameSessionState = Object.freeze({
      ...before,
      gameEpoch: before.gameEpoch + 1,
      updatedAtMs: atMs,
      position: snapshot,
      acceptedMoves: history,
      result: snapshot.result,
      clock: restoredClock,
      clockTimelineAnchors,
      promotionPending: undefined,
      ai: Object.freeze({ nextRequestId: before.ai.nextRequestId }),
    });
    const effects: GameSessionEffect[] = [];
    if (before.ai.pending) {
      effects.push({ type: 'cancel-ai', request: before.ai.pending });
    }
    effects.push(persistEffect(state));
    return changed(state, effects);
  }

  private resign(command: Extract<GameSessionCommand, { type: 'resign' }>): GameSessionTransition {
    if (
      command.gameEpoch !== this.current.gameEpoch ||
      this.current.review !== undefined ||
      this.current.result.terminal
    ) {
      return unchanged(this.current);
    }

    const atMs = toTimestamp(command.atMs, this.current.updatedAtMs);
    const clockTransition = this.flagIfExpired(atMs);
    if (clockTransition.state !== this.current) {
      return clockTransition;
    }

    const before = this.current;
    const resignedColor = resigningColor(before.mode, before.position.turn);
    const state: GameSessionState = Object.freeze({
      ...before,
      updatedAtMs: atMs,
      result: resignationFor(resignedColor),
      promotionPending: undefined,
      ai: Object.freeze({ nextRequestId: before.ai.nextRequestId }),
    });
    const effects: GameSessionEffect[] = [];
    if (before.ai.pending) {
      effects.push({ type: 'cancel-ai', request: before.ai.pending });
    }
    effects.push(persistEffect(state));
    return changed(state, effects);
  }

  private agreeDraw(command: Extract<GameSessionCommand, { type: 'agree-draw' }>): GameSessionTransition {
    if (
      command.gameEpoch !== this.current.gameEpoch ||
      this.current.mode.kind !== 'hot-seat' ||
      this.current.review !== undefined ||
      this.current.promotionPending !== undefined ||
      this.current.result.terminal
    ) {
      return unchanged(this.current);
    }

    const atMs = toTimestamp(command.atMs, this.current.updatedAtMs);
    const clockTransition = this.flagIfExpired(atMs);
    if (clockTransition.state !== this.current) {
      return clockTransition;
    }

    const state: GameSessionState = Object.freeze({
      ...this.current,
      updatedAtMs: atMs,
      result: Object.freeze({
        terminal: true,
        token: '1/2-1/2',
        reason: 'draw-agreement',
      }),
    });
    return changed(state, [persistEffect(state)]);
  }

  private newGame(command: Extract<GameSessionCommand, { type: 'new-game' }>): GameSessionTransition {
    if (
      command.expectedGameEpoch !== this.current.gameEpoch ||
      !isSetup(command.setup)
    ) {
      return unchanged(this.current);
    }

    const before = this.current;
    const atMs = toTimestamp(command.atMs ?? command.setup.startedAtMs, before.updatedAtMs);
    const reset = this.rules.reset(command.setup.initialFen);
    if (!reset.accepted) {
      return unchanged(before);
    }

    const state = createState({
      gameId: command.setup.gameId ?? `game-${before.gameEpoch + 1}`,
      createdAtMs: atMs,
      updatedAtMs: atMs,
      gameEpoch: before.gameEpoch + 1,
      position: reset.snapshot,
      mode: command.setup.mode,
      clock: command.setup.clock,
      nextRequestId: before.ai.nextRequestId,
    });
    const requested = this.requestAiIfNeeded(state);
    const effects: GameSessionEffect[] = [];
    if (before.ai.pending) {
      effects.push({ type: 'cancel-ai', request: before.ai.pending });
    }
    effects.push(persistEffect(requested.state), ...requested.effects);
    return changed(requested.state, effects);
  }

  private restoreRecord(
    command: Extract<GameSessionCommand, { type: 'restore-record' }>,
  ): GameSessionTransition {
    if (
      command.expectedGameEpoch !== this.current.gameEpoch ||
      !isGameSessionRecord(command.record)
    ) {
      return unchanged(this.current);
    }

    const record = command.record;
    const restoredRules = this.rulesFactory();
    const replay = restoredRules.replay({
      initialFen: record.initialFen,
      acceptedUciEvents: record.acceptedUciMoves,
    });
    if (!replay.accepted) {
      return unchanged(this.current);
    }

    const before = this.current;
    if (
      record.derivedFen !== replay.snapshot.fen ||
      (record.clock !== undefined && record.clock.activeColor !== replay.snapshot.turn) ||
      !hasValidPersistedTimeline(record, replay.snapshot) ||
      !isRestorableResult(record, replay.snapshot)
    ) {
      return unchanged(before);
    }
    const atMs = toTimestamp(command.atMs, record.updatedAtMs);
    const history = restoreHistory(record, replay.snapshot);
    // The current branch, not imported storage metadata, defines monotonicity.
    const nextEpoch = before.gameEpoch + 1;
    const base = createState({
      gameId: record.gameId,
      createdAtMs: record.createdAtMs,
      updatedAtMs: record.result.terminal ? record.updatedAtMs : atMs,
      gameEpoch: nextEpoch,
      position: replay.snapshot,
      acceptedMoves: history,
      mode: record.mode,
      clock: record.clock,
      clockTimelineAnchors: record.clockTimelineAnchors,
      nextRequestId: record.ai.nextRequestId,
    });
    const restoredResult = isClockAdjudicationRecord(record, base) ||
      isResignationRecord(record, base) ||
      isAgreementDrawRecord(record, base)
      ? record.result
      : base.result;
    const restored: GameSessionState = Object.freeze({ ...base, result: restoredResult });
    const flagged = this.flagStateIfExpired(restored, atMs);
    const requested = flagged.state.result.terminal
      ? { state: flagged.state, effects: [] as readonly GameSessionEffect[] }
      : this.requestAiIfNeeded(flagged.state);

    const effects: GameSessionEffect[] = [];
    if (before.ai.pending) {
      effects.push({ type: 'cancel-ai', request: before.ai.pending });
    }
    effects.push(persistEffect(requested.state), ...requested.effects);
    this.rules = restoredRules;
    return changed(requested.state, effects);
  }

  private cancelPromotion(
    command: Extract<GameSessionCommand, { type: 'cancel-promotion' }>,
  ): GameSessionTransition {
    const pending = this.current.promotionPending;
    if (
      pending === undefined ||
      !isLiveAt(this.current, command.gameEpoch, command.positionRevision) ||
      pending.gameEpoch !== command.gameEpoch ||
      pending.positionRevision !== command.positionRevision
    ) {
      return unchanged(this.current);
    }

    return changed(Object.freeze({ ...this.current, promotionPending: undefined }));
  }

  private selectReviewPly(
    command: Extract<GameSessionCommand, { type: 'select-review-ply' }>,
  ): GameSessionTransition {
    if (
      command.gameEpoch !== this.current.gameEpoch ||
      (this.current.clock !== undefined && !this.current.result.terminal) ||
      !Number.isSafeInteger(command.ply) ||
      command.ply < 0 ||
      command.ply > this.current.acceptedMoves.length ||
      this.current.review?.ply === command.ply
    ) {
      return unchanged(this.current);
    }

    const reviewRules = this.rulesFactory();
    const replay = reviewRules.replay({
      initialFen: this.current.position.initialFen,
      acceptedUciEvents: this.current.acceptedMoves.slice(0, command.ply).map((move) => move.uci),
    });
    if (!replay.accepted) {
      return unchanged(this.current);
    }

    const pending = this.current.ai.pending;
    const state: GameSessionState = Object.freeze({
      ...this.current,
      ...(pending
        ? { ai: Object.freeze({ nextRequestId: this.current.ai.nextRequestId }) }
        : {}),
      review: Object.freeze({ ply: command.ply, position: replay.snapshot }),
    });
    return changed(
      state,
      pending ? [{ type: 'cancel-ai', request: pending }] : [],
    );
  }

  private exitReview(command: Extract<GameSessionCommand, { type: 'exit-review' }>): GameSessionTransition {
    if (command.gameEpoch !== this.current.gameEpoch || this.current.review === undefined) {
      return unchanged(this.current);
    }

    const clockTransition = this.flagIfExpired(toTimestamp(command.atMs, this.current.updatedAtMs));
    if (clockTransition.state !== this.current) {
      return clockTransition;
    }

    const live: GameSessionState = Object.freeze({ ...this.current, review: undefined });
    return this.requestAiIfNeeded(live);
  }

  private reconcileClock(atMs: number): GameSessionTransition {
    if (!isTimestamp(atMs)) {
      return unchanged(this.current);
    }
    return this.flagIfExpired(atMs);
  }

  private commitAcceptedMove(
    before: GameSessionState,
    position: RulesSnapshot,
    uci: UciMove,
    san: string,
    actor: SessionMoveActor,
    atMs: number,
  ): GameSessionTransition {
    const committedClock = settleClockAfterMove(before.clock, before.position.turn, atMs);
    const accepted: AcceptedSessionMove = Object.freeze({
      uci,
      san,
      actor,
      committedAtMs: atMs,
      ...(before.clock ? { clockBefore: before.clock } : {}),
    });
    const state: GameSessionState = Object.freeze({
      ...before,
      updatedAtMs: atMs,
      position,
      acceptedMoves: Object.freeze([...before.acceptedMoves, accepted]),
      result: position.result,
      clock: committedClock,
      promotionPending: undefined,
      ai: Object.freeze({ nextRequestId: before.ai.nextRequestId }),
      review: undefined,
    });
    const requested = position.result.terminal
      ? { state, effects: [] as readonly GameSessionEffect[] }
      : this.requestAiIfNeeded(state);

    return changed(requested.state, [persistEffect(requested.state), ...requested.effects]);
  }

  private aiFailed(code: AiFailure['code'], requestId: number): GameSessionTransition {
    const state: GameSessionState = Object.freeze({
      ...this.current,
      ai: Object.freeze({
        nextRequestId: this.current.ai.nextRequestId,
        lastFailure: Object.freeze({ code, requestId }),
      }),
    });
    return changed(state, [persistEffect(state)]);
  }

  private requestAiIfNeeded(state: GameSessionState): GameSessionTransition {
    if (!isComputerControlledTurn(state) || state.result.terminal || state.ai.pending) {
      return unchanged(state);
    }

    const mode = state.mode;
    if (mode.kind !== 'computer') {
      return unchanged(state);
    }

    const request: AiRequestMetadata = Object.freeze({
      requestId: state.ai.nextRequestId,
      gameEpoch: state.gameEpoch,
      positionRevision: state.position.revision,
      positionFen: state.position.fen,
      ply: state.acceptedMoves.length,
      profileId: mode.ai.id,
      tierVersion: mode.ai.tierVersion,
      engineBuild: mode.ai.engineBuild,
      gameSeed: mode.ai.gameSeed,
    });
    const requested: GameSessionState = Object.freeze({
      ...state,
      ai: Object.freeze({
        nextRequestId: state.ai.nextRequestId + 1,
        pending: request,
      }),
    });
    return changed(requested, [{ type: 'request-ai', request }]);
  }

  private flagIfExpired(atMs: number): GameSessionTransition {
    const flagged = this.flagStateIfExpired(this.current, atMs);
    if (flagged.state === this.current) {
      return unchanged(this.current);
    }

    const effects: GameSessionEffect[] = [];
    if (this.current.ai.pending) {
      effects.push({ type: 'cancel-ai', request: this.current.ai.pending });
    }
    effects.push(persistEffect(flagged.state));
    return changed(flagged.state, effects);
  }

  private flagStateIfExpired(state: GameSessionState, atMs: number): { readonly state: GameSessionState } {
    const clock = state.clock;
    if (state.result.terminal || clock === undefined || atMs < clock.activeDeadlineMs) {
      return { state };
    }

    const result = flagFallResultFor(state.position, clock.activeColor);
    return {
      state: Object.freeze({
        ...state,
        updatedAtMs: atMs,
        result,
        promotionPending: undefined,
        ai: Object.freeze({ nextRequestId: state.ai.nextRequestId }),
      }),
    };
  }
}

export function createGameSession(
  setup: GameSessionSetup,
  dependencies?: GameSessionDependencies,
): GameSession {
  return new GameSession(setup, dependencies);
}

/** The rendered position is a past immutable snapshot only while reviewing. */
export function selectDisplayPosition(state: GameSessionState): RulesSnapshot {
  return state.review?.position ?? state.position;
}

/** Move lists track the reviewed point, while the live record remains intact. */
export function selectDisplayedMoves(state: GameSessionState): readonly AcceptedSessionMove[] {
  return state.review
    ? state.acceptedMoves.slice(0, state.review.ply)
    : state.acceptedMoves;
}

export function selectClockProjection(
  state: GameSessionState,
  atMs: number,
): ClockProjection | undefined {
  const clock = state.clock;
  if (clock === undefined) {
    return undefined;
  }

  const observedAtMs = toTimestamp(atMs, state.updatedAtMs);
  const activeRemaining = Math.max(0, clock.activeDeadlineMs - observedAtMs);
  return Object.freeze({
    whiteRemainingMs: clock.activeColor === 'w' ? activeRemaining : clock.whiteRemainingMs,
    blackRemainingMs: clock.activeColor === 'b' ? activeRemaining : clock.blackRemainingMs,
    activeColor: clock.activeColor,
    activeDeadlineMs: clock.activeDeadlineMs,
    flagged: activeRemaining === 0,
  });
}

export function selectBoardInputLocked(state: GameSessionState, atMs?: number): boolean {
  return (
    state.result.terminal ||
    state.review !== undefined ||
    state.promotionPending !== undefined ||
    state.ai.pending !== undefined ||
    (atMs !== undefined && selectClockProjection(state, atMs)?.flagged === true)
  );
}

export function selectLearnUndoPlies(state: GameSessionState): 0 | 1 | 2 {
  return learnUndoCount(state);
}

export function toGameSessionRecord(state: GameSessionState): GameSessionRecord {
  const record: GameSessionRecord = {
    schemaVersion: GAME_SESSION_RECORD_VERSION,
    gameId: state.gameId,
    createdAtMs: state.createdAtMs,
    updatedAtMs: state.updatedAtMs,
    gameEpoch: state.gameEpoch,
    initialFen: state.position.initialFen,
    acceptedUciMoves: Object.freeze(state.acceptedMoves.map((move) => move.uci)),
    acceptedMoves: Object.freeze(state.acceptedMoves.map(copyAcceptedMove)),
    derivedFen: state.position.fen,
    result: copyResult(state.result),
    mode: copyMode(state.mode),
    ...(state.clock ? { clock: copyClock(state.clock) } : {}),
    clockTimelineAnchors: Object.freeze(state.clockTimelineAnchors.map(copyClockTimelineAnchor)),
    ai: copyAiState(state.ai),
  };
  return Object.freeze(record);
}

function createState(input: {
  readonly gameId: string;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
  readonly gameEpoch: number;
  readonly position: RulesSnapshot;
  readonly mode: PlayerMode;
  readonly clock?: ClockSettings | GameClockState;
  readonly clockTimelineAnchors?: readonly ClockTimelineAnchor[];
  readonly acceptedMoves?: readonly AcceptedSessionMove[];
  readonly nextRequestId: number;
}): GameSessionState {
  const clock = toClockState(input.clock, input.position.turn, input.updatedAtMs);
  return Object.freeze({
    gameId: input.gameId,
    createdAtMs: input.createdAtMs,
    updatedAtMs: input.updatedAtMs,
    gameEpoch: input.gameEpoch,
    position: input.position,
    acceptedMoves: Object.freeze([...(input.acceptedMoves ?? [])]),
    mode: copyMode(input.mode),
    result: input.position.result,
    ...(clock ? { clock } : {}),
    clockTimelineAnchors: Object.freeze(
      (input.clockTimelineAnchors ?? []).map(copyClockTimelineAnchor),
    ),
    ai: Object.freeze({ nextRequestId: input.nextRequestId }),
  });
}

function restoreHistory(record: GameSessionRecord, position: RulesSnapshot): readonly AcceptedSessionMove[] {
  const initialRules = position.turn;
  // Turn after n plies lets us infer initial turn without reaching into chess.js.
  const initialTurn = record.acceptedUciMoves.length % 2 === 0
    ? initialRules
    : opposite(initialRules);

  return Object.freeze(record.acceptedUciMoves.map((uci, index) => {
    const persisted = record.acceptedMoves[index];
    const color = index % 2 === 0 ? initialTurn : opposite(initialTurn);
    const actor = actorForColor(record.mode, color);
    return Object.freeze({
      uci,
      san: position.sanHistory[index] ?? '',
      actor,
      committedAtMs: persisted?.committedAtMs ?? record.updatedAtMs,
      ...(persisted?.clockBefore ? { clockBefore: copyClock(persisted.clockBefore) } : {}),
    });
  }));
}

/**
 * Re-derives every persisted clock boundary from the initial settings. A
 * structurally valid but semantically spliced clockBefore must never reach
 * learn undo, where it could otherwise resurrect time from another branch.
 */
function hasValidPersistedTimeline(record: GameSessionRecord, position: RulesSnapshot): boolean {
  if (record.updatedAtMs < record.createdAtMs) {
    return false;
  }

  const initialTurn = record.acceptedUciMoves.length % 2 === 0
    ? position.turn
    : opposite(position.turn);
  const anchors = record.clockTimelineAnchors ?? [];
  let previousCommittedAtMs = record.createdAtMs;

  if (record.clock === undefined) {
    return anchors.length === 0 && record.acceptedMoves.every((move, index) => {
      const mover = index % 2 === 0 ? initialTurn : opposite(initialTurn);
      const valid = (
        move.clockBefore === undefined &&
        move.actor === actorForColor(record.mode, mover) &&
        move.committedAtMs >= previousCommittedAtMs &&
        move.committedAtMs <= record.updatedAtMs
      );
      previousCommittedAtMs = move.committedAtMs;
      return valid;
    });
  }

  const initialDeadlineMs = record.createdAtMs + record.clock.initialMs;
  if (!isTimestamp(initialDeadlineMs)) {
    return false;
  }
  let expected: GameClockState = Object.freeze({
    initialMs: record.clock.initialMs,
    incrementMs: record.clock.incrementMs,
    whiteRemainingMs: record.clock.initialMs,
    blackRemainingMs: record.clock.initialMs,
    activeColor: initialTurn,
    activeDeadlineMs: initialDeadlineMs,
  });
  let anchorIndex = 0;

  for (const [index, move] of record.acceptedMoves.entries()) {
    const anchored = applyClockTimelineAnchor(
      expected,
      anchors[anchorIndex],
      index,
      previousCommittedAtMs,
      record.updatedAtMs,
    );
    if (anchored === false) {
      return false;
    }
    if (anchored !== undefined) {
      expected = anchored.clock;
      previousCommittedAtMs = anchored.atMs;
      anchorIndex += 1;
    }

    const mover = index % 2 === 0 ? initialTurn : opposite(initialTurn);
    if (
      move.clockBefore === undefined ||
      move.actor !== actorForColor(record.mode, mover) ||
      mover !== expected.activeColor ||
      !sameClock(move.clockBefore, expected) ||
      move.committedAtMs < previousCommittedAtMs ||
      move.committedAtMs > record.updatedAtMs ||
      move.committedAtMs >= expected.activeDeadlineMs
    ) {
      return false;
    }
    expected = settleClockAfterMove(expected, mover, move.committedAtMs)!;
    previousCommittedAtMs = move.committedAtMs;
  }

  const finalAnchor = applyClockTimelineAnchor(
    expected,
    anchors[anchorIndex],
    record.acceptedMoves.length,
    previousCommittedAtMs,
    record.updatedAtMs,
  );
  if (finalAnchor === false) {
    return false;
  }
  if (finalAnchor !== undefined) {
    expected = finalAnchor.clock;
    anchorIndex += 1;
  }

  return anchorIndex === anchors.length && sameClock(record.clock, expected);
}

function applyClockTimelineAnchor(
  expected: GameClockState,
  anchor: ClockTimelineAnchor | undefined,
  ply: number,
  previousAtMs: number,
  updatedAtMs: number,
): { readonly clock: GameClockState; readonly atMs: number } | false | undefined {
  if (anchor === undefined) {
    return undefined;
  }
  if (anchor.ply !== ply) {
    return anchor.ply < ply ? false : undefined;
  }

  const active = expected.activeColor;
  const inactive = opposite(active);
  const activeRemainingMs = remainingFor(anchor.clock, active);
  const anchoredDeadlineMs = anchor.atMs + activeRemainingMs;
  if (
    anchor.atMs < previousAtMs ||
    anchor.atMs > updatedAtMs ||
    !isTimestamp(anchoredDeadlineMs) ||
    anchor.clock.initialMs !== expected.initialMs ||
    anchor.clock.incrementMs !== expected.incrementMs ||
    anchor.clock.activeColor !== active ||
    remainingFor(anchor.clock, inactive) !== remainingFor(expected, inactive) ||
    activeRemainingMs > remainingFor(expected, active) ||
    anchor.clock.activeDeadlineMs !== anchoredDeadlineMs
  ) {
    return false;
  }

  return { clock: anchor.clock, atMs: anchor.atMs };
}

function remainingFor(clock: GameClockState, color: RulesColor): number {
  return color === 'w' ? clock.whiteRemainingMs : clock.blackRemainingMs;
}

function sameClock(left: GameClockState, right: GameClockState): boolean {
  return (
    left.initialMs === right.initialMs &&
    left.incrementMs === right.incrementMs &&
    left.whiteRemainingMs === right.whiteRemainingMs &&
    left.blackRemainingMs === right.blackRemainingMs &&
    left.activeColor === right.activeColor &&
    left.activeDeadlineMs === right.activeDeadlineMs
  );
}

function isClockAdjudicationRecord(record: GameSessionRecord, state: GameSessionState): boolean {
  return (
    record.result.terminal &&
    record.clock !== undefined &&
    record.clock.activeDeadlineMs <= record.updatedAtMs &&
    !state.position.result.terminal &&
    sameSessionResult(
      record.result,
      flagFallResultFor(state.position, record.clock.activeColor),
    )
  );
}

function isResignationRecord(record: GameSessionRecord, state: GameSessionState): boolean {
  return (
    record.result.terminal &&
    record.result.reason === 'resignation' &&
    !state.position.result.terminal
  );
}

function isAgreementDrawRecord(
  record: GameSessionRecord,
  state: Pick<GameSessionState, 'position'>,
): boolean {
  return (
    record.mode.kind === 'hot-seat' &&
    record.result.terminal &&
    record.result.token === '1/2-1/2' &&
    record.result.reason === 'draw-agreement' &&
    !state.position.result.terminal &&
    (record.clock === undefined || record.clock.activeDeadlineMs > record.updatedAtMs)
  );
}

/** Stored terminal metadata is only accepted when it agrees with replayed rules. */
function isRestorableResult(record: GameSessionRecord, position: RulesSnapshot): boolean {
  if (record.result.reason === 'resignation') {
    return (
      !position.result.terminal &&
      sameSessionResult(record.result, resignationFor(resigningColor(record.mode, position.turn)))
    );
  }
  if (isAgreementDrawRecord(record, { position })) {
    // Agreement is session metadata, distinct from RulesPort's canonical
    // draws. An expired clock is handled by the independently reproducible
    // flag-fall branch below instead.
    return true;
  }
  if (
    record.clock !== undefined &&
    record.clock.activeDeadlineMs <= record.updatedAtMs &&
    !position.result.terminal
  ) {
    return sameSessionResult(
      record.result,
      flagFallResultFor(position, record.clock.activeColor),
    );
  }

  return sameSessionResult(record.result, position.result);
}

function sameSessionResult(left: SessionResult, right: SessionResult): boolean {
  if (
    left.terminal !== right.terminal ||
    left.token !== right.token ||
    left.reason !== right.reason
  ) {
    return false;
  }

  return left.terminal && right.terminal && 'winner' in left && 'winner' in right
    ? left.winner === right.winner
    : true;
}

function settleClockAfterMove(
  clock: GameClockState | undefined,
  movingColor: RulesColor,
  atMs: number,
): GameClockState | undefined {
  if (clock === undefined) {
    return undefined;
  }

  const movingRemaining = Math.max(0, clock.activeDeadlineMs - atMs) + clock.incrementMs;
  const whiteRemainingMs = movingColor === 'w' ? movingRemaining : clock.whiteRemainingMs;
  const blackRemainingMs = movingColor === 'b' ? movingRemaining : clock.blackRemainingMs;
  const activeColor = opposite(movingColor);
  const activeRemaining = activeColor === 'w' ? whiteRemainingMs : blackRemainingMs;

  return Object.freeze({
    initialMs: clock.initialMs,
    incrementMs: clock.incrementMs,
    whiteRemainingMs,
    blackRemainingMs,
    activeColor,
    activeDeadlineMs: atMs + activeRemaining,
  });
}

function rebaseClock(clock: GameClockState, moveAtMs: number, nowMs: number): GameClockState {
  const activeRemaining = Math.max(0, clock.activeDeadlineMs - moveAtMs);
  const whiteRemainingMs = clock.activeColor === 'w' ? activeRemaining : clock.whiteRemainingMs;
  const blackRemainingMs = clock.activeColor === 'b' ? activeRemaining : clock.blackRemainingMs;
  return Object.freeze({
    ...clock,
    whiteRemainingMs,
    blackRemainingMs,
    activeDeadlineMs: nowMs + activeRemaining,
  });
}

function toClockState(
  input: ClockSettings | GameClockState | undefined,
  activeColor: RulesColor,
  atMs: number,
): GameClockState | undefined {
  if (input === undefined) {
    return undefined;
  }
  if (isGameClockState(input)) {
    return input.activeColor === activeColor ? copyClock(input) : undefined;
  }
  if (!isClockSettings(input)) {
    return undefined;
  }
  return Object.freeze({
    initialMs: input.initialMs,
    incrementMs: input.incrementMs,
    whiteRemainingMs: input.initialMs,
    blackRemainingMs: input.initialMs,
    activeColor,
    activeDeadlineMs: atMs + input.initialMs,
  });
}

function learnUndoCount(state: GameSessionState): 0 | 1 | 2 {
  if (
    state.review !== undefined ||
    state.result.terminal ||
    state.mode.kind !== 'computer' ||
    !state.mode.learnMode
  ) {
    return 0;
  }

  const last = state.acceptedMoves.at(-1);
  if (state.ai.pending && last?.actor === 'human') {
    return 1;
  }
  const previous = state.acceptedMoves.at(-2);
  return last?.actor === 'computer' && previous?.actor === 'human' ? 2 : 0;
}

function isLiveAt(
  state: GameSessionState,
  gameEpoch: number,
  positionRevision: number,
): boolean {
  return (
    gameEpoch === state.gameEpoch &&
    positionRevision === state.position.revision &&
    state.review === undefined &&
    !state.result.terminal
  );
}

function isHumanControlledTurn(state: GameSessionState): boolean {
  return state.mode.kind === 'hot-seat' || state.position.turn === state.mode.humanColor;
}

function isComputerControlledTurn(state: GameSessionState): boolean {
  return (
    state.mode.kind === 'computer' &&
    state.position.turn !== state.mode.humanColor &&
    state.review === undefined &&
    state.promotionPending === undefined &&
    !state.result.terminal
  );
}

function actorForTurn(state: GameSessionState): SessionMoveActor {
  return state.mode.kind === 'hot-seat'
    ? 'hot-seat'
    : state.position.turn === state.mode.humanColor
      ? 'human'
      : 'computer';
}

function actorForColor(mode: PlayerMode, color: RulesColor): SessionMoveActor {
  return mode.kind === 'hot-seat' ? 'hot-seat' : color === mode.humanColor ? 'human' : 'computer';
}

function matchesAiRequest(
  request: AiRequestMetadata,
  command: Extract<GameSessionCommand, { type: 'resolve-ai' }>,
): boolean {
  return (
    request.requestId === command.requestId &&
    request.gameEpoch === command.gameEpoch &&
    request.positionRevision === command.positionRevision &&
    request.positionFen === command.positionFen
  );
}

function timeoutFor(flaggedColor: RulesColor): TimeoutResult {
  const winner = opposite(flaggedColor);
  return Object.freeze({
    terminal: true,
    token: winner === 'w' ? '1-0' : '0-1',
    reason: 'timeout',
    winner,
  });
}

/**
 * FIDE flag-fall depends on whether the other side could checkmate by any
 * possible legal series. CapyChess currently proves only the universally safe
 * subset: a side with a bare king can never checkmate. All other material is
 * deliberately left to the ordinary timeout result rather than overclaimed.
 */
function flagFallResultFor(position: RulesSnapshot, flaggedColor: RulesColor): SessionResult {
  const wouldBeWinner = opposite(flaggedColor);
  if (position.nonKingPieceCount[wouldBeWinner] === 0) {
    return Object.freeze({
      terminal: true,
      token: '1/2-1/2',
      reason: 'draw',
    });
  }
  return timeoutFor(flaggedColor);
}

function resignationFor(resignedColor: RulesColor): ResignationResult {
  const winner = opposite(resignedColor);
  return Object.freeze({
    terminal: true,
    token: winner === 'w' ? '1-0' : '0-1',
    reason: 'resignation',
    winner,
  });
}

function resigningColor(mode: PlayerMode, turn: RulesColor): RulesColor {
  return mode.kind === 'computer' ? mode.humanColor : turn;
}

function opposite(color: RulesColor): RulesColor {
  return color === 'w' ? 'b' : 'w';
}

function unchanged(state: GameSessionState): GameSessionTransition {
  return Object.freeze({ state, effects: Object.freeze([]) });
}

function changed(
  state: GameSessionState,
  effects: readonly GameSessionEffect[] = [],
): GameSessionTransition {
  return Object.freeze({ state, effects: Object.freeze([...effects]) });
}

function persistEffect(state: GameSessionState): GameSessionEffect {
  return Object.freeze({ type: 'persist', record: toGameSessionRecord(state) });
}

function isSetup(value: unknown): value is GameSessionSetup {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const setup = value as Partial<GameSessionSetup>;
  return (
    isPlayerMode(setup.mode) &&
    (setup.initialFen === undefined || typeof setup.initialFen === 'string') &&
    (setup.gameId === undefined || (typeof setup.gameId === 'string' && setup.gameId.length > 0)) &&
    (setup.startedAtMs === undefined || isTimestamp(setup.startedAtMs)) &&
    (setup.clock === undefined || isClockSettings(setup.clock))
  );
}

function isPlayerMode(value: unknown): value is PlayerMode {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const mode = value as Partial<PlayerMode> & { ai?: Partial<ComputerAiProfile> };
  if (mode.kind === 'hot-seat') {
    return true;
  }
  return (
    mode.kind === 'computer' &&
    (mode.humanColor === 'w' || mode.humanColor === 'b') &&
    (mode.humanColorPreference === undefined ||
      mode.humanColorPreference === 'w' ||
      mode.humanColorPreference === 'b' ||
      mode.humanColorPreference === 'random') &&
    typeof mode.learnMode === 'boolean' &&
    mode.ai !== undefined &&
    typeof mode.ai.id === 'string' && mode.ai.id.length > 0 &&
    typeof mode.ai.tierVersion === 'string' && mode.ai.tierVersion.length > 0 &&
    typeof mode.ai.engineBuild === 'string' && mode.ai.engineBuild.length > 0 &&
    typeof mode.ai.gameSeed === 'string' && mode.ai.gameSeed.length > 0
  );
}

function isClockSettings(value: unknown): value is ClockSettings {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const clock = value as Partial<ClockSettings>;
  return (
    typeof clock.initialMs === 'number' && Number.isSafeInteger(clock.initialMs) && clock.initialMs > 0 &&
    typeof clock.incrementMs === 'number' && Number.isSafeInteger(clock.incrementMs) && clock.incrementMs >= 0
  );
}

function isGameClockState(value: unknown): value is GameClockState {
  if (!isClockSettings(value)) {
    return false;
  }
  const clock = value as Partial<GameClockState>;
  return (
    isTimestamp(clock.whiteRemainingMs) &&
    isTimestamp(clock.blackRemainingMs) &&
    (clock.activeColor === 'w' || clock.activeColor === 'b') &&
    isTimestamp(clock.activeDeadlineMs)
  );
}

function isGameSessionRecord(value: unknown): value is GameSessionRecord {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const record = value as Partial<GameSessionRecord>;
  return (
    record.schemaVersion === GAME_SESSION_RECORD_VERSION &&
    typeof record.gameId === 'string' && record.gameId.length > 0 &&
    isTimestamp(record.createdAtMs) &&
    isTimestamp(record.updatedAtMs) &&
    typeof record.gameEpoch === 'number' && Number.isSafeInteger(record.gameEpoch) && record.gameEpoch >= 0 &&
    typeof record.initialFen === 'string' &&
    Array.isArray(record.acceptedUciMoves) && record.acceptedUciMoves.every(isUci) &&
    Array.isArray(record.acceptedMoves) &&
    record.acceptedMoves.length === record.acceptedUciMoves.length &&
    record.acceptedMoves.every(isAcceptedMove) &&
    record.acceptedMoves.every((move, index) => move.uci === record.acceptedUciMoves?.[index]) &&
    typeof record.derivedFen === 'string' &&
    isSessionResult(record.result) &&
    isPlayerMode(record.mode) &&
    (record.clock === undefined || isGameClockState(record.clock)) &&
    (record.clockTimelineAnchors === undefined || (
      Array.isArray(record.clockTimelineAnchors) &&
      record.clockTimelineAnchors.every(isClockTimelineAnchor)
    )) &&
    isAiState(record.ai)
  );
}

function isClockTimelineAnchor(value: unknown): value is ClockTimelineAnchor {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const anchor = value as Partial<ClockTimelineAnchor>;
  return (
    typeof anchor.ply === 'number' &&
    Number.isSafeInteger(anchor.ply) &&
    anchor.ply >= 0 &&
    isTimestamp(anchor.atMs) &&
    isGameClockState(anchor.clock)
  );
}

function isAcceptedMove(value: unknown): value is AcceptedSessionMove {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const move = value as Partial<AcceptedSessionMove>;
  return (
    typeof move.uci === 'string' && isUci(move.uci) &&
    typeof move.san === 'string' &&
    (move.actor === 'hot-seat' || move.actor === 'human' || move.actor === 'computer') &&
    isTimestamp(move.committedAtMs) &&
    (move.clockBefore === undefined || isGameClockState(move.clockBefore))
  );
}

function isSessionResult(value: unknown): value is SessionResult {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const result = value as Partial<SessionResult>;
  if (result.terminal === false) {
    return result.token === '*' && result.reason === 'ongoing';
  }
  if (result.reason === 'timeout' || result.reason === 'resignation') {
    return (
      result.terminal === true &&
      ((result.token === '1-0' && result.winner === 'w') ||
        (result.token === '0-1' && result.winner === 'b'))
    );
  }
  if (result.reason === 'checkmate') {
    return (
      result.terminal === true &&
      (result.token === '1-0' || result.token === '0-1') &&
      (result.winner === 'w' || result.winner === 'b')
    );
  }
  return (
    result.terminal === true &&
    result.token === '1/2-1/2' &&
    (result.reason === 'stalemate' ||
      result.reason === 'threefold-repetition' ||
      result.reason === 'fifty-move-rule' ||
      result.reason === 'insufficient-material' ||
      result.reason === 'draw' ||
      result.reason === 'draw-agreement')
  );
}

function isAiState(value: unknown): value is GameSessionAiState {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const ai = value as Partial<GameSessionAiState>;
  return (
    typeof ai.nextRequestId === 'number' &&
    Number.isSafeInteger(ai.nextRequestId) && ai.nextRequestId > 0 &&
    (ai.pending === undefined || isAiRequest(ai.pending)) &&
    (ai.lastFailure === undefined || isAiFailure(ai.lastFailure))
  );
}

function isAiRequest(value: unknown): value is AiRequestMetadata {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const request = value as Partial<AiRequestMetadata>;
  return (
    typeof request.requestId === 'number' && Number.isSafeInteger(request.requestId) && request.requestId > 0 &&
    typeof request.gameEpoch === 'number' && Number.isSafeInteger(request.gameEpoch) && request.gameEpoch >= 0 &&
    typeof request.positionRevision === 'number' && Number.isSafeInteger(request.positionRevision) && request.positionRevision >= 0 &&
    typeof request.positionFen === 'string' &&
    typeof request.ply === 'number' && Number.isSafeInteger(request.ply) && request.ply >= 0 &&
    typeof request.profileId === 'string' &&
    typeof request.tierVersion === 'string' &&
    typeof request.engineBuild === 'string' &&
    typeof request.gameSeed === 'string'
  );
}

function isAiFailure(value: unknown): value is AiFailure {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const failure = value as Partial<AiFailure>;
  return (
    (failure.code === 'empty-result' || failure.code === 'invalid-uci' || failure.code === 'illegal-move') &&
    typeof failure.requestId === 'number' && Number.isSafeInteger(failure.requestId) && failure.requestId > 0
  );
}

function isUci(value: unknown): value is UciMove {
  return typeof value === 'string' && UCI_PATTERN.test(value);
}

function isTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function toTimestamp(value: unknown, fallback: number): number {
  // Commands cannot rewind a deadline by carrying an older callback timestamp.
  return isTimestamp(value) ? Math.max(value, fallback) : fallback;
}

function copyMode(mode: PlayerMode): PlayerMode {
  return mode.kind === 'hot-seat'
    ? Object.freeze({ kind: 'hot-seat' as const })
    : Object.freeze({
        kind: 'computer' as const,
        humanColor: mode.humanColor,
        humanColorPreference: mode.humanColorPreference ?? mode.humanColor,
        learnMode: mode.learnMode,
        ai: Object.freeze({ ...mode.ai }),
      });
}

function copyClock(clock: GameClockState): GameClockState {
  return Object.freeze({ ...clock });
}

function copyAcceptedMove(move: AcceptedSessionMove): AcceptedSessionMove {
  return Object.freeze({
    ...move,
    ...(move.clockBefore ? { clockBefore: copyClock(move.clockBefore) } : {}),
  });
}

function copyClockTimelineAnchor(anchor: ClockTimelineAnchor): ClockTimelineAnchor {
  return Object.freeze({
    ply: anchor.ply,
    atMs: anchor.atMs,
    clock: copyClock(anchor.clock),
  });
}

function copyResult(result: SessionResult): SessionResult {
  return Object.freeze({ ...result }) as SessionResult;
}

function copyAiState(ai: GameSessionAiState): GameSessionAiState {
  return Object.freeze({
    nextRequestId: ai.nextRequestId,
    ...(ai.pending ? { pending: Object.freeze({ ...ai.pending }) } : {}),
    ...(ai.lastFailure ? { lastFailure: Object.freeze({ ...ai.lastFailure }) } : {}),
  });
}
